/**
 * TOSS Web Application — Client portal: one answer to «назад»
 *
 * The portal used to have three, and they disagreed. The full-screen bar's arrow went to a
 * `fallbackTo` prop set separately on twelve screens; the edge swipe went to `/client` always;
 * the conversation had a third copy. On «Шаблоны» the arrow led to «Профиль» and the swipe to
 * «Главная» — two gestures meaning the same thing, two destinations. All three also asked
 * `window.history.length > 1`, which counts the whole TAB: opened from a link in a letter, the
 * arrow returned the customer to their mail.
 *
 * The rule, and it is the one the device's own back button follows:
 *
 *   **Back goes back.** If the entry behind belongs to this application, pop it — the customer
 *   returns to the screen they came from, with its filters, its scroll position and whatever
 *   they had typed. This is what makes an application card opened from the chat return to the
 *   chat, and the same card opened from «Документы» return to «Документы».
 *
 *   **Otherwise go up.** With nothing of ours behind, there is no «back» to perform, so the
 *   arrow offers the screen this one sits inside ({@link clientRouteParent}) — and REPLACES
 *   rather than pushes, so a customer who arrived on a deep link does not build a fake history
 *   they can walk back down into the screen they just left.
 *
 * Every back control of the portal calls this: the two phone app bars, the conversation header,
 * the edge swipe and the computer's `PageHeader`. A screen that must ask before leaving does not
 * intercept it — it blocks the navigation with `useBlocker`, so the device's back button and the
 * swipe are covered too, which an intercepted arrow never was.
 */

import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants';
import { hasInAppHistory } from '@/utils/appHistory';
import { clientRouteParent } from './clientRouteRegistry';

export interface ClientBack {
  /** Go back — or up, when there is nothing of ours behind. */
  goBack: () => void;
  /**
   * The screen this one sits inside. For rendering a real `href` under the arrow (a middle
   * click, «открыть в новой вкладке», a screen reader announcing a destination) — never for
   * deciding where an ordinary click leads.
   */
  parentPath: string;
  /** Whether this screen has anything above it. `false` on the five addresses of the bottom bar. */
  hasParent: boolean;
}

export function useClientBack(): ClientBack {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const parent = useMemo(() => clientRouteParent(pathname), [pathname]);

  const goBack = useCallback((): void => {
    // Read at click time rather than at render time. Between the two, a guard's redirect or a
    // page's own `replace` may have moved the history under this component, and a stale answer
    // here is either a dead arrow or a jump out of the app.
    if (hasInAppHistory()) {
      navigate(-1);
      return;
    }

    navigate(parent ?? ROUTES.client.dashboard, { replace: true });
  }, [navigate, parent]);

  return {
    goBack,
    parentPath: parent ?? ROUTES.client.dashboard,
    hasParent: parent !== null,
  };
}

export default useClientBack;
